import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { BedIcon, PriceTagIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getDatesDuration } from '../../../api/products/get-product-dates'
import {
  AcademicZoneEnum,
  academicZoneEnumMapping
} from '../../../lib/types/enums/staff/academic-zone.enum'
import { ProductDateFormInput } from '../../../lib/types/models/product/product-date-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createEnumArraySchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../elements/validation-buttons'
import { getDurationInDaysString } from './utils'

type Props = BaseFormProps<ProductDateFormInput, true> & {
  className?: string
  disableDateFields?: boolean
}

const formSchema: ZodType<ProductDateFormInput> = z
  .object({
    departureDate: createStringSchema('date de départ'),
    returnDate: createStringSchema('date de retour'),
    basePrice: createNumberSchema('prix', { decimalPlaces: 2, isPositive: true }),
    academicZones: createEnumArraySchema({
      enumType: AcademicZoneEnum,
      fieldName: 'zones académiques'
    })
  })
  .superRefine((data, ctx) => {
    if (data.departureDate && data.returnDate) {
      if (new Date(data.departureDate) > new Date(data.returnDate)) {
        return ctx.addIssue({
          path: ['departureDate'],
          message: 'La date de départ doit être avant la date de retour',
          code: z.ZodIssueCode.custom
        })
      }
    }
  })

const ProductDateForm = ({
  className,
  defaultValues,
  disableDateFields = false,
  ...validationButtonProps
}: Props) => {
  const form = useForm<ProductDateFormInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })
  const { departureDate, returnDate } = form.watch()

  const { data: duration } = useQuery({
    queryKey: ['dates-duration', departureDate, returnDate],
    queryFn: () => getDatesDuration({ departureDate, returnDate }),
    enabled: !!departureDate && !!returnDate
  })

  const startDate = departureDate ? new Date(departureDate) : undefined

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <FormField
            control={form.control}
            name={'departureDate'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    type="appointment"
                    mode={'over'}
                    label={'Date de départ'}
                    disabledPastDates
                    disabled={disableDateFields}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'returnDate'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    startDate={startDate}
                    type="appointment"
                    mode={'over'}
                    label={'Date de retour'}
                    disabledPastDates
                    disabled={disableDateFields}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'academicZones'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    variant="primary"
                    label={'Zones académiques'}
                    options={Object.values(AcademicZoneEnum)
                      .filter((zone) => zone !== AcademicZoneEnum.NONE)
                      .map((zone) => ({
                        label: academicZoneEnumMapping[zone],
                        value: zone
                      }))}
                    selectedOptions={
                      field.value
                        ? field.value.map((zone) => ({
                            label: academicZoneEnumMapping[zone],
                            value: zone
                          }))
                        : []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'basePrice'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    required
                    {...field}
                    type="number"
                    label="Prix de base (€)"
                    icon={PriceTagIcon}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {duration && duration > 0 && (
            <div className="flex justify-center bg-secondary-light gap-2 items-center py-2 rounded">
              <BedIcon color={tailwindColors.secondary.DEFAULT} width="18px" height="18px" />
              <Typography variant="label-semibold">{getDurationInDaysString(duration)}</Typography>
            </div>
          )}
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default ProductDateForm
